import { Ban, CalendarDays, CalendarX, Pencil, ShieldCheck, Users, Zap } from "lucide-react";
import { Pill } from "@/modules/escala/components/Pill";
import { formatarQuando } from "@/modules/escala/format";
import type { Escala } from "@/modules/escala/services/escalaService";

interface CabecalhoEscalaProps {
  escala: Escala & { ministerioNome: string };
  /** Só quem lidera/gerencia o ministério dono e só em escala ativa — quem decide é a página. */
  onEditar?: () => void;
  onCancelar?: () => void;
}

/** Cartão de topo da escala: nome, quando, ministério, regra de confirmação, descrição e (para quem gere) editar/cancelar. */
export function CabecalhoEscala({ escala, onEditar, onCancelar }: CabecalhoEscalaProps) {
  const cancelada = escala.situacao === "cancelada";
  const ConfirmacaoIcone = escala.exige_confirmacao ? ShieldCheck : Zap;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start gap-2">
        <h1 className="min-w-0 flex-1 break-words text-xl font-extrabold leading-tight text-text sm:text-2xl">{escala.nome}</h1>
        {cancelada && (
          <Pill tom="off" icone={Ban}>
            Cancelada
          </Pill>
        )}
      </div>

      <ul className="flex flex-col gap-1.5 text-[14px] text-text">
        <li className="flex items-start gap-2">
          <CalendarDays size={17} aria-hidden className="mt-0.5 shrink-0 text-muted" />
          {formatarQuando(escala.data_hora)}
        </li>
        <li className="flex items-start gap-2">
          <Users size={17} aria-hidden className="mt-0.5 shrink-0 text-muted" />
          {escala.ministerioNome}
        </li>
        <li className="flex items-start gap-2">
          <ConfirmacaoIcone size={17} aria-hidden className="mt-0.5 shrink-0 text-muted" />
          {escala.exige_confirmacao
            ? "Cada participante confirma a própria presença"
            : "Participantes entram já confirmados"}
        </li>
      </ul>

      {escala.descricao && <p className="break-words text-[14px] text-text">{escala.descricao}</p>}

      {cancelada && (
        <p className="flex items-start gap-2.5 rounded-xl border border-danger/30 bg-danger/5 px-3.5 py-3 text-[13.5px] leading-snug text-text">
          <CalendarX size={18} aria-hidden className="mt-px shrink-0 text-danger" />
          <span className="min-w-0 break-words">
            <b>Escala cancelada.</b>
            {escala.motivo_cancelamento ? ` Motivo: ${escala.motivo_cancelamento}` : ""}
          </span>
        </p>
      )}

      {(onEditar || onCancelar) && (
        <div className="flex flex-wrap gap-2">
          {onEditar && (
            <button
              type="button"
              onClick={onEditar}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-border px-4 text-sm font-semibold text-text transition-colors hover:bg-surface-2"
            >
              <Pencil size={16} aria-hidden /> Editar
            </button>
          )}
          {onCancelar && (
            <button
              type="button"
              onClick={onCancelar}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-danger/40 px-4 text-sm font-semibold text-danger transition-colors hover:bg-danger/10"
            >
              <Ban size={16} aria-hidden /> Cancelar escala
            </button>
          )}
        </div>
      )}
    </div>
  );
}
